<template>
  <div class="p-4">
    <!-- Aviso: los dependientes se guardan junto con el colaborador; documentos van después -->
    <wz-alert variant="info" icon="ri-information-line" class="mb-4">
      Los datos de familia se guardarán junto con el colaborador. Los documentos de cada dependiente se
      pueden agregar después, desde el detalle del colaborador.
    </wz-alert>

    <!-- Cónyuge / Conviviente -->
    <div class="family-section">
      <div class="dep-section-header">
        <h6 class="text-muted mb-0">Cónyuge / Conviviente</h6>
        <wz-button v-if="!spouse" variant="primary" size="sm" @click="openCreate('CONYUGE')">
          <i class="ri-add-line mr-1"></i>Registrar cónyuge
        </wz-button>
      </div>
      <div v-if="spouse" class="dep-card-list">
        <dependent-card :dependent="spouse" draft @edit="openEdit" @deactivate="removeDependent" />
      </div>
      <p v-else class="text-muted mb-0">No hay cónyuge/conviviente registrado</p>
    </div>

    <!-- Padres / Madres -->
    <div class="family-section">
      <div class="dep-section-header">
        <h6 class="text-muted mb-0">Padres / Madres</h6>
        <wz-button variant="primary" size="sm" @click="openCreate('OTRO')">
          <i class="ri-add-line mr-1"></i>Agregar padre/madre
        </wz-button>
      </div>
      <div v-if="parents.length > 0" class="dep-card-list">
        <dependent-card v-for="p in parents" :key="p._tid" :dependent="p" draft
                        @edit="openEdit" @deactivate="removeDependent" />
      </div>
      <p v-else class="text-muted mb-0">No hay padres registrados</p>
    </div>

    <!-- Hijos -->
    <div class="family-section">
      <div class="dep-section-header">
        <h6 class="text-muted mb-0">Hijos</h6>
        <wz-button variant="primary" size="sm" @click="openCreate('HIJO')">
          <i class="ri-add-line mr-1"></i>Agregar hijo
        </wz-button>
      </div>
      <div v-if="children.length > 0" class="dep-card-list">
        <dependent-card v-for="c in children" :key="c._tid" :dependent="c" draft show-child-fields
                        @edit="openEdit" @deactivate="removeDependent" />
      </div>
      <p v-else class="text-muted mb-0">No hay hijos registrados</p>
    </div>

    <!-- Modal alta/edición de dependiente (en memoria) -->
    <wz-modal v-model="modalDependent" :title="modalTitle" size="lg" :close-on-overlay="false">
      <div class="flex flex-wrap -mx-2">
        <!-- Parentesco (no HIJO) -->
        <div class="w-full md:w-1/2 px-2" v-if="modalType !== 'HIJO'">
          <div class="mb-3">
            <label>Parentesco <span class="text-danger">*</span></label>
            <wz-select :items="otherTypeOptions" v-model="modalForm.relationshipType" defaultOption="Seleccione" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Tipo de documento <span class="text-danger">*</span></label>
            <wz-select :items="docTypeOptions" v-model="modalForm.documentType" defaultOption="Seleccione" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Número de documento <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.documentNumber" class="wz-form-control" placeholder="Número de documento" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Nombres <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.firstName" class="wz-form-control" placeholder="Nombres" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Apellido paterno <span class="text-danger">*</span></label>
            <input type="text" v-model="modalForm.lastNamePaternal" class="wz-form-control" placeholder="Apellido paterno" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Apellido materno</label>
            <input type="text" v-model="modalForm.lastNameMaternal" class="wz-form-control" placeholder="Apellido materno" />
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Fecha de nacimiento <span class="text-danger">*</span></label>
            <wz-datepicker v-model="modalForm.birthDate" />
            <small v-if="modalAge !== null" class="text-muted">{{ modalAge }} años</small>
          </div>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <div class="mb-3">
            <label>Género</label>
            <wz-select :items="genderOptions" v-model="modalForm.gender" defaultOption="Seleccione" />
          </div>
        </div>

        <!-- Campos extra para HIJO -->
        <template v-if="modalType === 'HIJO'">
          <div class="w-full md:w-1/2 px-2" v-if="modalAge !== null && modalAge >= 18">
            <div class="mb-3">
              <label><input type="checkbox" v-model="modalForm.hasStudyCertificate" class="mr-1" />Constancia de estudios</label>
            </div>
          </div>
          <div class="w-full md:w-1/2 px-2" v-if="modalForm.hasStudyCertificate && modalAge >= 18">
            <div class="mb-3">
              <label>Vencimiento de constancia <span class="text-danger">*</span></label>
              <wz-datepicker v-model="modalForm.studyCertificateExpiry" />
            </div>
          </div>
          <div class="w-full md:w-1/2 px-2">
            <div class="mb-3">
              <label><input type="checkbox" v-model="modalForm.hasDisability" class="mr-1" />Tiene discapacidad</label>
            </div>
          </div>
        </template>

        <!-- Documento sustentatorio (no HIJO) -->
        <template v-if="modalType !== 'HIJO'">
          <div class="w-full md:w-1/2 px-2">
            <div class="mb-3">
              <label>Tipo de documento sustentatorio</label>
              <input type="text" v-model="modalForm.supportingDocumentType" class="wz-form-control"
                     placeholder="Ej: Partida de matrimonio / Acta de convivencia" />
            </div>
          </div>
          <div class="w-full md:w-1/2 px-2">
            <div class="mb-3">
              <label>Número de documento sustentatorio</label>
              <input type="text" v-model="modalForm.supportingDocumentNumber" class="wz-form-control" placeholder="Número (opcional)" />
            </div>
          </div>
        </template>

        <div class="w-full px-2">
          <div class="mb-3">
            <label>Observaciones</label>
            <wz-textarea v-model="modalForm.notes" rows="2" placeholder="Observaciones (opcional)" />
          </div>
        </div>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalDependent = false">Cancelar</wz-button>
        <wz-button :disabled="!canSaveModal" @click="saveDependent">
          {{ editingTid ? 'Guardar cambios' : 'Agregar' }}
        </wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Constants } from '@/core/utils'
import DependentCard from '@/modules/Employees/components/DependentCard.vue'

export default {
  name: 'FamilyDraftTab',
  components: { DependentCard },
  props: {
    // v-model: lista de dependientes (shape de EmployeeDependentRequest). El padre la envía en el alta.
    value: { type: Array, default: () => [] },
  },
  data() {
    return {
      items: [],          // estado interno con _tid + campos derivados (fullName, age) para las tarjetas
      tidSeq: 1,
      modalDependent: false,
      modalType: 'HIJO',
      editingTid: null,
      modalForm: this.emptyForm(),
      otherTypeOptions: [
        { key: 'CONYUGE',     label: 'Cónyuge' },
        { key: 'CONVIVIENTE', label: 'Conviviente' },
        { key: 'PADRE',       label: 'Padre' },
        { key: 'MADRE',       label: 'Madre' },
      ],
      docTypeOptions: [
        Constants.IdentifierType.DNI,
        Constants.IdentifierType.CE,
        Constants.IdentifierType.PASAPORTE,
      ],
      genderOptions: [
        { key: 'M', label: 'Masculino' },
        { key: 'F', label: 'Femenino' },
      ],
    }
  },
  created() {
    // Hidrata el estado interno desde el v-model (por si el padre trae datos previos).
    this.items = (this.value || []).map(d => this.decorate({ ...d }))
  },
  computed: {
    spouse() {
      return this.items.find(d => d.relationshipType === 'CONYUGE' || d.relationshipType === 'CONVIVIENTE') || null
    },
    parents() {
      return this.items
        .filter(d => d.relationshipType === 'PADRE' || d.relationshipType === 'MADRE')
        .sort((a, b) => (a.relationshipType || '').localeCompare(b.relationshipType || ''))
    },
    children() {
      return this.items
        .filter(d => d.relationshipType === 'HIJO')
        .sort((a, b) => (a.age || 0) - (b.age || 0))
    },
    modalTitle() {
      if (this.modalType === 'HIJO') return this.editingTid ? 'Editar hijo' : 'Registrar hijo'
      return this.editingTid ? 'Editar familiar' : 'Registrar familiar'
    },
    modalAge() {
      return this.ageFrom(this.modalForm.birthDate)
    },
    canSaveModal() {
      const f = this.modalForm
      if (!f.documentType || !f.documentNumber || !f.firstName || !f.lastNamePaternal || !f.birthDate) return false
      if (this.modalType !== 'HIJO' && !f.relationshipType) return false
      if (this.modalType === 'HIJO' && f.hasStudyCertificate && this.modalAge >= 18 && !f.studyCertificateExpiry) return false
      return true
    },
  },
  methods: {
    emptyForm() {
      return {
        relationshipType: null, documentType: null, documentNumber: null,
        firstName: null, lastNamePaternal: null, lastNameMaternal: null,
        birthDate: null, gender: null, hasStudyCertificate: false,
        studyCertificateExpiry: null, hasDisability: false,
        supportingDocumentType: null, supportingDocumentNumber: null, notes: null,
      }
    },
    ageFrom(birthDate) {
      if (!birthDate) return null
      const birth = new Date(birthDate + 'T00:00:00')
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const m = today.getMonth() - birth.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
      return age >= 0 ? age : 0
    },
    // Agrega campos derivados usados por DependentCard (no se envían al backend).
    decorate(dep) {
      return {
        ...dep,
        _tid: dep._tid || this.tidSeq++,
        age: this.ageFrom(dep.birthDate),
        fullName: [dep.firstName, dep.lastNamePaternal, dep.lastNameMaternal].filter(Boolean).join(' '),
      }
    },
    openCreate(type) {
      this.editingTid = null
      this.modalType = type
      this.modalForm = this.emptyForm()
      if (type === 'HIJO') this.modalForm.relationshipType = 'HIJO'
      else if (type === 'OTRO') this.modalForm.relationshipType = 'PADRE'
      else this.modalForm.relationshipType = 'CONYUGE'
      this.modalDependent = true
    },
    openEdit(dep) {
      this.editingTid = dep._tid
      this.modalType = dep.relationshipType === 'HIJO' ? 'HIJO' : 'OTRO'
      this.modalForm = {
        relationshipType: dep.relationshipType, documentType: dep.documentType, documentNumber: dep.documentNumber,
        firstName: dep.firstName, lastNamePaternal: dep.lastNamePaternal, lastNameMaternal: dep.lastNameMaternal,
        birthDate: dep.birthDate, gender: dep.gender, hasStudyCertificate: dep.hasStudyCertificate || false,
        studyCertificateExpiry: dep.studyCertificateExpiry, hasDisability: dep.hasDisability || false,
        supportingDocumentType: dep.supportingDocumentType, supportingDocumentNumber: dep.supportingDocumentNumber,
        notes: dep.notes,
      }
      this.modalDependent = true
    },
    saveDependent() {
      if (!this.canSaveModal) return
      const data = { ...this.modalForm }
      if (this.modalType === 'HIJO') data.relationshipType = 'HIJO'
      if (!data.hasStudyCertificate) data.studyCertificateExpiry = null
      if (this.editingTid) {
        const idx = this.items.findIndex(i => i._tid === this.editingTid)
        if (idx !== -1) this.items.splice(idx, 1, this.decorate({ ...data, _tid: this.editingTid }))
      } else {
        this.items.push(this.decorate(data))
      }
      this.modalDependent = false
      this.emitChange()
    },
    removeDependent(dep) {
      this.items = this.items.filter(i => i._tid !== dep._tid)
      this.emitChange()
    },
    // Emite la lista limpia (solo campos del request) al padre vía v-model.
    emitChange() {
      const clean = this.items.map(({ _tid, age, fullName, ...req }) => req) // eslint-disable-line no-unused-vars
      this.$emit('input', clean)
    },
  },
}
</script>

<style scoped>
.family-section { margin-bottom: 24px; }
.dep-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.dep-card-list { display: flex; flex-direction: column; gap: 12px; }
</style>
